import { ActivityQuestions, MemberAnswerType } from "../../typings/activity";
import React, { useEffect, useRef, useState } from "react";
import './../../pages/course/activity/partials/quiz.scss';
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { LOADING_UI, UNLOADING_UI } from "../../redux/types";
import { authenticatedRequestRaw } from "../../services/api.service";
import { toast } from 'react-toastify';
import { CompetencyTestDetailType } from "../../typings/competency-test";

interface IQuiz {
	activity: CompetencyTestDetailType;
	timeOver: boolean;
}

const Quiz: React.FC<IQuiz> = (props) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const answerText = useRef("");
	const { activity } = props;
	const questionsLength = activity.questions.length;

	let memberAnswerDefault: any[] = activity.questions.map((q) => {
		let defaultVal: any = '';
		if (q.type === 'pgk_mcma') defaultVal = [];
		if (q.type === 'pgk_kategori') defaultVal = {};
		return {
			id: q.id,
			isAnswered: 0,
			answer: defaultVal,
			score: q.score,
		};
	});

	if (activity.memberTest?.member_answer) {
		try {
			const savedAnswers = JSON.parse(activity.memberTest.member_answer);
			if (Array.isArray(savedAnswers) && savedAnswers.length > 0) {
				memberAnswerDefault = memberAnswerDefault.map(def => {
					const saved = savedAnswers.find((s: any) => s.id === def.id);
					if (saved) {
						return { ...def, isAnswered: saved.isAnswered, answer: saved.answer };
					}
					return def;
				});
			}
		} catch (e) {
			console.error("Failed to parse saved answers", e);
		}
	}

	const [question, setQuestion] = useState<ActivityQuestions>();
	const [questionIndex, setQuestionIndex] = useState<number>(0);
	const [disablePagePrev, setDisablePagePrev] = useState<boolean>(false);
	const [disablePageNext, setDisablePageNext] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [memberAnswers, setMemberAnswers] = useState<MemberAnswerType[]>(memberAnswerDefault);
	const [modalFinish, showModalFinish] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [savedText, setSavedText] = useState('');
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		
		const handler = setTimeout(async () => {
			setIsSaving(true);
			setSavedText('Menyimpan...');
			try {
				await authenticatedRequestRaw('POST', '/competency-test/autosave', {
					test_id: activity?.id,
					answers: memberAnswers,
				});
				setSavedText('✔ Tersimpan');
			} catch (err) {
				setSavedText('⚠ Gagal Menyimpan');
			} finally {
				setIsSaving(false);
				setTimeout(() => {
					setSavedText('');
				}, 3000);
			}
		}, 1000);

		return () => {
			clearTimeout(handler);
		};
	}, [memberAnswers, activity?.id]);

	useEffect(() => {
		const currentQ = activity.questions[questionIndex];
		setQuestion(currentQ);
		if (currentQ?.type === 'essay') {
			answerText.current = memberAnswers[questionIndex]?.answer || '';
		}

		setDisablePagePrev(questionIndex === 0);
		setDisablePageNext(questionIndex === (questionsLength - 1));
	}, [questionIndex]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (props.timeOver) {
			submitQuiz();
		}
	}, [props.timeOver]); // eslint-disable-line react-hooks/exhaustive-deps

	// Single choice handler
	const handleAnswer = (index: string) => {
		setMemberAnswers(memberAnswers.map((a, idx) => {
			return { ...a, ...(questionIndex === idx ? { isAnswered: 1, answer: index } : {}) };
		}));
	};

	// MCMA checkbox handler (multi-select)
	const handleMcmaAnswer = (index: string) => {
		const currentAns = Array.isArray(memberAnswers[questionIndex]?.answer)
			? [...memberAnswers[questionIndex].answer]
			: [];
		const exists = currentAns.includes(index);
		const nextAns = exists
			? currentAns.filter((i) => i !== index)
			: [...currentAns, index].sort();

		setMemberAnswers(memberAnswers.map((a, idx) => {
			return {
				...a,
				...(questionIndex === idx
					? { isAnswered: nextAns.length > 0 ? 1 : 0, answer: nextAns }
					: {}),
			};
		}));
	};

	// Category matrix table handler
	const handleKategoriAnswer = (rowId: string, colVal: string) => {
		const currentMap =
			typeof memberAnswers[questionIndex]?.answer === 'object' &&
			memberAnswers[questionIndex]?.answer !== null &&
			!Array.isArray(memberAnswers[questionIndex]?.answer)
				? { ...memberAnswers[questionIndex].answer }
				: {};

		currentMap[rowId] = colVal;
		let rawOpts = question?.options as any;
		if (typeof rawOpts === 'string') {
			try { rawOpts = JSON.parse(rawOpts); } catch {}
		}
		const totalRows = rawOpts?.rows?.length || 0;
		const isFullyAnswered = totalRows > 0 && Object.keys(currentMap).length >= totalRows;

		setMemberAnswers(memberAnswers.map((a, idx) => {
			return {
				...a,
				...(questionIndex === idx
					? { isAnswered: isFullyAnswered ? 1 : (Object.keys(currentMap).length > 0 ? 1 : 0), answer: currentMap }
					: {}),
			};
		}));
	};

	const handleChangeText = (e: any) => {
		answerText.current = e.target.value;
	};

	const handleEssayAnswer = () => {
		if (question?.type === 'essay') {
			const memberAnswerIndex = memberAnswers.findIndex((a) => a.id === question.id);
			const memberAnswerClone = [...memberAnswers];
			memberAnswerClone[memberAnswerIndex].isAnswered = answerText.current !== '' ? 1 : 0;
			memberAnswerClone[memberAnswerIndex].answer = answerText.current;
			setMemberAnswers([...memberAnswerClone]);
			answerText.current = '';
		}
	};

	const DisplayOptions = () => {
		if (question) {
			const memberAnswer = memberAnswers[questionIndex];
			const opts = Array.isArray(question.options) ? question.options : [];
			return (
				<div className={'options-wrapper d-flex flex-column w-100'}>
					{opts.map((opt: any, idx: number) => (
						<div
							className={`options-item flex w-full cursor-pointer mb-2 ${
								memberAnswer?.isAnswered && memberAnswer?.answer === opt.index ? 'selected' : ''
							}`}
							onClick={() => handleAnswer(opt.index)}
							key={`opt-${questionIndex}-${idx}`}
						>
							<div className={'opt-index'}>{opt.index}</div>
							{question?.type === 'pg' ? (
								<div className={'opt-text leading-snug'} dangerouslySetInnerHTML={{ __html: opt.text }} />
							) : (
								<img
									src={opt.text}
									alt="Quiz option"
									style={{ maxWidth: '200px', border: '1px solid #d9d9d9', borderRadius: '10px', padding: '8px' }}
								/>
							)}
						</div>
					))}
				</div>
			);
		}
		return null;
	};

	const DisplayMcma = () => {
		if (question) {
			const memberAnswer = memberAnswers[questionIndex];
			const currentSelected: string[] = Array.isArray(memberAnswer?.answer) ? memberAnswer.answer : [];
			const opts = Array.isArray(question.options) ? question.options : [];

			return (
				<div className={'options-wrapper d-flex flex-column w-100'}>
					<div className={'text-xs font-semibold text-gray-500 mb-2 italic'}>
						* Pilihan Ganda Kompleks: Anda dapat memilih lebih dari satu jawaban yang benar.
					</div>
					{opts.map((opt: any, idx: number) => {
						const isChecked = currentSelected.includes(opt.index);
						return (
							<div
								className={`flex items-center w-full cursor-pointer mb-2 rounded-lg border transition-colors p-4 ${
									isChecked ? 'bg-blue-50 border-blue-400' : 'bg-white border-gray-200 hover:bg-gray-50'
								}`}
								onClick={() => handleMcmaAnswer(opt.index)}
								key={`mcma-${questionIndex}-${idx}`}
							>
								<div className="flex-shrink-0 mr-4 flex items-center justify-center">
									<input
										type="checkbox"
										checked={isChecked}
										readOnly
										className="w-5 h-5 text-blue-600 rounded border-gray-300 cursor-pointer pointer-events-none"
									/>
								</div>
								<div className={'text-gray-800 leading-snug'} dangerouslySetInnerHTML={{ __html: opt.text }} />
							</div>
						);
					})}
				</div>
			);
		}
		return null;
	};

	const DisplayKategori = () => {
		if (question) {
			const memberAnswer = memberAnswers[questionIndex];
			const currentMap =
				typeof memberAnswer?.answer === 'object' && memberAnswer?.answer !== null && !Array.isArray(memberAnswer?.answer)
					? memberAnswer.answer
					: {};

			let rawOpts = question.options as any;
			if (typeof rawOpts === 'string') {
				try {
					rawOpts = JSON.parse(rawOpts);
				} catch {}
			}
			const columns: string[] = rawOpts?.columns || ['Benar', 'Salah'];
			const rows: any[] = rawOpts?.rows || [];

			return (
				<div className={'w-full'}>
					<div className={'text-xs font-semibold text-gray-500 mb-3 italic'}>
						* Tentukan pilihan untuk setiap pernyataan berikut:
					</div>
					<div className={'table-responsive'}>
						<table className={'w-full text-left border-collapse border border-gray-200 rounded-lg overflow-hidden'}>
							<thead>
								<tr className={'bg-gray-100 text-gray-800 text-sm'}>
									<th className={'p-3 border border-gray-200'}>Pernyataan</th>
									{columns.map((col, cIdx) => (
										<th key={`col-${cIdx}`} className={'p-3 border border-gray-200 text-center w-28'}>
											{col}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{rows.map((row: any, rIdx: number) => {
									const rowKey = String(row.id ?? rIdx);
									const selectedVal = currentMap[rowKey];
									return (
										<tr key={`row-${rIdx}`} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
											<td className={'p-3 border border-gray-200 text-sm leading-relaxed'}>
												<div dangerouslySetInnerHTML={{ __html: row.text }} />
											</td>
											{columns.map((col, cIdx) => {
												const isSelected = selectedVal === col;
												return (
													<td
														key={`cell-${rIdx}-${cIdx}`}
														className={`p-3 border border-gray-200 text-center cursor-pointer hover:bg-blue-50 transition-colors ${
															isSelected ? 'bg-blue-100/50 font-bold' : ''
														}`}
														onClick={() => handleKategoriAnswer(rowKey, col)}
													>
														<input
															type="radio"
															name={`kategori-row-${questionIndex}-${rowKey}`}
															checked={isSelected}
															onChange={() => handleKategoriAnswer(rowKey, col)}
															className="w-4 h-4 text-blue-600 cursor-pointer"
														/>
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			);
		}
		return null;
	};

	const DisplayInput = () => {
		if (question) {
			return (
				<div className={'question-input'}>
					<textarea
						defaultValue={memberAnswers[questionIndex]?.answer || ''}
						rows={3}
						placeholder={'Masukkan jawaban...'}
						onChange={handleChangeText}
					/>
				</div>
			);
		}
		return null;
	};

	const displayForm = () => {
		if (question) {
			switch (question.type) {
				case 'image':
				case 'pg':
					return <DisplayOptions />;
				case 'pgk_mcma':
					return <DisplayMcma />;
				case 'pgk_kategori':
					return <DisplayKategori />;
				case 'essay':
					return <DisplayInput />;
				default:
					return <DisplayOptions />;
			}
		}
		return null;
	};

	const Navigation = () => {
		const perPage = 5;
		const qLength = activity.questions.length;
		const totalPaginationPage = Math.ceil(qLength / perPage);
		const offset = (currentPage - 1) * perPage;
		let maxPage = currentPage * perPage;
		if (currentPage === totalPaginationPage) {
			maxPage = qLength - (maxPage - perPage) + (currentPage - 1) * perPage;
		}
		const pages = Array.from({ length: maxPage - offset }, (_, i) => offset + 1 + i);

		return (
			<div className={'quiz-navigation fixed bottom-0 left-0 w-full bg-white shadow-lg border-t py-3 z-30'}>
				<div className={'flex flex-col w-full'}>
					<div className={'flex flex-wrap w-full justify-center mb-2 px-2'}>
						{pages.map((p, idx) => {
							const isCurrent = (p - 1) === questionIndex;
							const isAnswered = memberAnswers[p - 1]?.isAnswered === 1;
							return (
								<button
									key={`page-btn-${idx}`}
									type="button"
									onClick={() => {
										handleEssayAnswer();
										setQuestionIndex(p - 1);
									}}
									className={`w-9 h-9 mx-1 my-1 rounded-lg text-xs font-bold transition-all border ${
										isCurrent
											? 'border-blue-600 bg-blue-600 text-white shadow-md'
											: isAnswered
											? 'bg-blue-100 text-blue-900 border-blue-300'
											: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
									}`}
								>
									{p}
								</button>
							);
						})}
					</div>
					<div className={'flex w-full justify-between items-center px-4'}>
						<div className={'flex items-center gap-2'}>
							<button
								type="button"
								disabled={currentPage <= 1}
								onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
								className="px-2.5 py-1 text-xs border rounded bg-white text-gray-700 disabled:opacity-40 hover:bg-gray-50 transition"
							>
								◀
							</button>
							<span className="text-xs text-gray-600 font-semibold">
								Halaman {currentPage} dari {totalPaginationPage}
							</span>
							<button
								type="button"
								disabled={currentPage >= totalPaginationPage}
								onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPaginationPage))}
								className="px-2.5 py-1 text-xs border rounded bg-white text-gray-700 disabled:opacity-40 hover:bg-gray-50 transition"
							>
								▶
							</button>
						</div>
						<Button primary onClick={confirmQuiz} className={'text-xs px-4 py-2'}>
							Selesaikan Tes
						</Button>
					</div>
				</div>
			</div>
		);
	};

	const onPagePrev = () => {
		handleEssayAnswer();
		if (questionIndex > 0) {
			setQuestionIndex(questionIndex - 1);
		}
	};

	const onPageNext = () => {
		handleEssayAnswer();
		if (questionIndex < questionsLength - 1) {
			setQuestionIndex(questionIndex + 1);
		}
	};

	const confirmQuiz = () => {
		handleEssayAnswer();
		showModalFinish(true);
	};

	const submitQuiz = async () => {
		showModalFinish(false);
		dispatch({ type: LOADING_UI });
		try {
			await authenticatedRequestRaw('POST', '/competency-test/submit', {
				test_id: activity?.id,
				answers: memberAnswers,
			});
			history.push(`/competency/${activity?.id}`);
			toast.success('Berhasil mengirim data tes', {
				position: toast.POSITION.TOP_CENTER,
			});
		} catch (err: any) {
			toast.error(err?.response?.data?.message ?? 'Gagal mengirim jawaban tes', {
				position: toast.POSITION.TOP_CENTER,
			});
		} finally {
			dispatch({ type: UNLOADING_UI });
		}
	};

	return (
		<>
			<div className={'quiz-container flex flex-col w-full'}>
				{/* Shared Stimulus / Wacana Panel */}
				{question?.stimulus ? (
					<div className={'border border-blue-200 rounded-xl p-4 mb-3 bg-blue-50/40 shadow-sm'}>
						<div className={'flex flex-col md:flex-row md:items-center items-start mb-3 gap-2 md:gap-3'}>
							<span className={'inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full uppercase'}>
								{question.stimulus.type !== 'text' ? question.stimulus.type : 'Wacana Bacaan'}
							</span>
							{question.stimulus.title ? (
								<h3 className={'font-bold text-lg text-slate-800 m-0 leading-tight'}>{question.stimulus.title}</h3>
							) : null}
						</div>
						<div
							className={'stimulus-text text-sm md:text-base leading-relaxed text-gray-800 space-y-2'}
							style={{ maxHeight: '350px', overflowY: 'auto' }}
							dangerouslySetInnerHTML={{ __html: question.stimulus.content }}
						/>
					</div>
				) : null}

				{/* Question Prompt Card */}
				<div className={'border rounded-xl relative bg-card shadow-sm'}>
					{/* Header Information */}
					<div className={'bg-slate-50 border-b rounded-t-xl p-3 flex flex-wrap items-center justify-between gap-2'}>
						{savedText && (
							<div className={`absolute top-2 right-4 text-xs font-bold px-2 py-1 rounded ${isSaving ? 'bg-orange-100 text-orange-600' : savedText.includes('Gagal') ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
								{savedText}
							</div>
						)}
						<div className={'flex items-center gap-2'}>
							<span className={'bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded'}>
								Soal No. {questionIndex + 1} / {questionsLength}
							</span>
							<span className={'bg-slate-200 text-slate-700 text-xs font-semibold px-3 py-1 rounded'}>
								{question?.type === 'pg' ? 'Pilihan Ganda' : question?.type === 'pgk_mcma' ? 'Pilihan Ganda Kompleks (Centang)' : question?.type === 'pgk_kategori' ? 'Pilihan Ganda Kompleks (Tabel)' : question?.type === 'essay' ? 'Uraian' : question?.type}
							</span>
						</div>
						{question?.metadata?.kompetensi && (
							<span className={'border border-blue-200 text-blue-700 text-xs font-semibold bg-white px-3 py-1 rounded'}>
								{question.metadata.kompetensi}
							</span>
						)}
					</div>
					
					{/* Subkompetensi (if available) */}
					{question?.metadata?.subkompetensi && (
						<div className={'border-b px-4 py-2 bg-blue-50/30 flex items-center gap-2'}>
							<div className="w-4 h-4 rounded-full border-2 border-blue-500 flex items-center justify-center">
								<div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
							</div>
							<span className={'text-xs text-blue-900 font-medium'}>
								Subkompetensi: <span className="font-semibold">{question.metadata.subkompetensi}</span>
							</span>
						</div>
					)}

					<div className={'p-4 text-sm md:text-base leading-relaxed text-gray-900'} dangerouslySetInnerHTML={{ __html: question?.text || '' }} />
				</div>

				{/* Answer Options Card */}
				<div className={'relative pt-3 mb-4'}>
					{question ? (
						<div className={'question-answer-wrapper bg-card p-4 rounded-xl border shadow-sm'}>
							{displayForm()}
						</div>
					) : null}
				</div>

				{/* Bottom Navigation Buttons */}
				<div className={'flex w-full justify-center mb-8'}>
					<Button primary className={'mr-2'} disabled={disablePagePrev} onClick={onPagePrev}>
						Sebelumnya
					</Button>
					<Button primary className={'ml-2'} disabled={disablePageNext} onClick={onPageNext}>
						Selanjutnya
					</Button>
				</div>

				<Navigation />
			</div>

			<Modal
				show={modalFinish}
				onHide={() => {
					showModalFinish(false);
				}}
				centered={true}
			>
				<Modal.Body>
					<div className="text-center p-3">
						<h4 className="font-bold text-lg mb-2">Konfirmasi Pengumpulan</h4>
						<p className="text-gray-600 text-sm">
							Anda telah menjawab{' '}
							<span className="font-bold text-blue-600">
								{memberAnswers.filter((a) => a.isAnswered === 1).length}
							</span>{' '}
							dari {questionsLength} soal. Apakah Anda yakin ingin menyelesaikan tes ini sekarang?
						</p>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						outline
						onClick={() => {
							showModalFinish(false);
						}}
					>
						Periksa Lagi
					</Button>
					<Button primary className={'min-w-75px gradient-primary'} onClick={submitQuiz}>
						Kirim Sekarang
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default Quiz;
